import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import CircularProgress from '@material-ui/core/CircularProgress'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import ButtonCta from '@components/atoms/button-cta'
import CategoryTabs from '@components/category-tabs'
import InfoCardVariant from '@components/info-and-image-card-with-overlay/info-card-variant'
import JsonLd from '@components/atoms/json-ld'
import SliderNew from '@components/slider-new'
import CoachCardOptimized from '@components/coach-card-optimized'
import CoachLandingArticles from '@components/coach-landing-articles'
import useDebounce from '@hooks/useDebounce'
import * as amplitude from '@amplitude/analytics-browser'
import defaults from '@config/defaults'
import { autocompleteCity } from '@lib/geo-api'
import { resolveLocationOptionFromInput, mapAlgoliaCityHitToLocationOption } from '@lib/pga-coach/resolve-journey-location'
import { buildPgaCoachJourneyContent } from '@lib/pga-coach/content'
import { COACH_RAIL_SLIDER_CONFIG } from '@lib/pga-coach/coach-rail'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import {
  PGA_COACH_JOURNEY_ANALYTICS,
  PGA_COACH_LANDING_GUIDES_SECTION_ID,
  PGA_COACH_PROGRAMS_SEARCH_PATHNAME,
} from '@lib/pga-coach/constants'
import {
  buildCityProgramsHref,
  buildIndexableJourneySearchUrl,
  buildJourneyOfferingCardSearchUrl,
  buildJourneySearchUrl,
  formatJourneyHeading,
  getDefaultJourneyPersona,
  getJourneyPersona,
  hasUrlSpecifiedJourneySearchState,
  loadJourneyLastSearchState,
  normalizeJourneySearchState,
  pickJourneyLocationContextState,
  saveJourneyLastSearchState,
} from '@lib/pga-coach/search-state'
import { detectJourneyLocationFromIp, searchLocalCoaches } from '@lib/pga-coach/offering-search'
import useStyles from './pga-coach-journey.styles'

const JOURNEY_CARD_RAIL_SPACING = {
  mobile: 16,
  tablet: 20,
  desktop: 24,
}

const LEGACY_COACH_DIRECTORY_HREF = '/coaches/search'

function JourneySearchControls({
  classes,
  searchState,
  heroPersonaValue,
  onPersonaChange,
  onLocationInputChange,
  onLocationSelect,
  locationOptions,
  locationInputValue,
  isDetectingLocation,
  onSubmit,
  submitLabel,
  secondaryLinkLabel,
}) {
  const selectedHeroPersona = locationOptions.personas.find((persona) => persona.id === heroPersonaValue)
  const personaTriggerLabel = selectedHeroPersona?.heroLabel || 'How can we help?'

  return (
    <>
      <Paper elevation={0} className={classes.searchShell}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} className={classes.searchFieldCell}>
            <Box className={classes.searchField}>
              <Select
                value={heroPersonaValue}
                onChange={(event) => onPersonaChange(event.target.value, 'hero')}
                className={classes.searchSelect}
                disableUnderline
                displayEmpty
                renderValue={() => personaTriggerLabel}
                inputProps={{ 'aria-label': 'Coach topic' }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  classes: { paper: classes.searchMenuPaper },
                }}
              >
                {locationOptions.personas.map((persona) => (
                  <MenuItem className={classes.searchMenuItem} key={persona.id} value={persona.id}>
                    {persona.heroLabel}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} className={classes.searchFieldCell}>
            <Box className={classes.searchField}>
              <RoomOutlinedIcon className={classes.searchLocationIcon} />
              <Autocomplete
                autoHighlight
                freeSolo
                forcePopupIcon={false}
                className={classes.searchAutocomplete}
                classes={{
                  paper: classes.searchMenuPaper,
                  option: classes.searchMenuItem,
                }}
                filterOptions={(options) => options}
                options={locationOptions.locations}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                inputValue={locationInputValue}
                onInputChange={(event, value, reason) => {
                  if (reason === 'reset') return
                  onLocationInputChange(value)
                }}
                onChange={(event, option) => {
                  if (!option) return
                  onLocationSelect(option)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="standard"
                    placeholder="City, State or ZIP"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: `${params.InputProps.className || ''} ${classes.searchAutocompleteInput}`.trim(),
                      endAdornment: (
                        <>
                          {isDetectingLocation ? <CircularProgress size={18} color="secondary" /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    /* eslint-disable-next-line react/jsx-no-duplicate-props -- merge Autocomplete params.inputProps with a11y label */
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': 'Coach location',
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item container xs={12} md={4} className={classes.searchButtonCell}>
            <ButtonCta
              color="primary"
              variant="contained"
              className={classes.searchButton}
              href={buildIndexableJourneySearchUrl(searchState)}
              onClick={onSubmit}
            >
              <SearchIcon style={{ marginRight: 8 }} />
              {submitLabel}
            </ButtonCta>
          </Grid>
        </Grid>
      </Paper>
      <Box textAlign="center">
        <ButtonCta
          variant="text"
          color="primary"
          href={LEGACY_COACH_DIRECTORY_HREF}
          className={classes.heroLink}
        >
          {secondaryLinkLabel}
        </ButtonCta>
      </Box>
    </>
  )
}

const normalizePersonaKey = (value = '') => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '')

/** All normalized keys tied to a persona (route id, tab label, editorial tag aliases). */
const normalizedPersonaIdentifiers = (persona) => {
  const raw = [persona?.id, persona?.label, ...(persona?.guideTagLabels || [])]
  return [...new Set(raw.map((value) => normalizePersonaKey(value)).filter(Boolean))]
}

const personaMatchesNormalizedTags = (persona, normalizedTags) => (
  normalizedTags.some((tag) => normalizedPersonaIdentifiers(persona).includes(tag))
)

JourneySearchControls.propTypes = {
  classes: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  heroPersonaValue: PropTypes.string.isRequired,
  onPersonaChange: PropTypes.func.isRequired,
  onLocationInputChange: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  locationOptions: PropTypes.shape({
    locations: PropTypes.array.isRequired,
    personas: PropTypes.array.isRequired,
  }).isRequired,
  locationInputValue: PropTypes.string.isRequired,
  isDetectingLocation: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string.isRequired,
  secondaryLinkLabel: PropTypes.string.isRequired,
}

function PgaCoachJourney({ ssrData, query }) {
  const classes = useStyles()
  const theme = useTheme()
  const isGuidesMobileLayout = useMediaQuery(theme.breakpoints.down('sm'))
  const showJourneyRailControls = useMediaQuery(theme.breakpoints.down('sm'))
  const isCoachJourneyXsLayout = useMediaQuery(theme.breakpoints.down('xs'))
  const content = useMemo(() => buildPgaCoachJourneyContent(ssrData), [ssrData])
  const offeringRailSliderConfig = useMemo(() => ({
    loop: false,
    mode: 'snap',
    slides: { origin: 'center', perView: 1, spacing: JOURNEY_CARD_RAIL_SPACING.mobile },
    breakpoints: {
      '(min-width: 600px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.tablet },
      },
      '(min-width: 960px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.desktop },
      },
      '(min-width: 1280px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.desktop },
      },
    },
    renderMode: 'performance',
  }), [])
  const guidesRailSliderConfig = useMemo(() => ({
    initial: 0,
    loop: false,
    mode: 'snap',
    slides: { origin: 'center', perView: 1, spacing: JOURNEY_CARD_RAIL_SPACING.mobile },
    breakpoints: {
      '(min-width: 600px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.tablet },
      },
      '(min-width: 960px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.desktop },
      },
      '(min-width: 1280px)': {
        slides: { perView: 'auto', spacing: JOURNEY_CARD_RAIL_SPACING.desktop },
      },
    },
  }), [])
  const routeSearchState = useMemo(() => normalizeJourneySearchState({
    persona: query?.persona,
    placeName: query?.placeName,
    aroundLatLng: query?.aroundLatLng,
  }), [query])
  const [searchState, setSearchState] = useState(routeSearchState)
  const [heroPersonaValue, setHeroPersonaValue] = useState(query?.persona || '')
  const [locationInputValue, setLocationInputValue] = useState(routeSearchState.placeName || '')
  const [locationOptions, setLocationOptions] = useState([])
  const locationOptionsRef = useRef(locationOptions)
  useEffect(() => {
    locationOptionsRef.current = locationOptions
  }, [locationOptions])
  const [isDetectingLocation, setIsDetectingLocation] = useState(!routeSearchState.placeName)
  const [localCoaches, setLocalCoaches] = useState([])
  const [isLoadingCoaches, setIsLoadingCoaches] = useState(true)
  const debouncedLocationInput = useDebounce(locationInputValue, 250)
  const journeyTabsPersonaId = searchState.persona || getDefaultJourneyPersona()
  const linkSearchState = useMemo(() => pickJourneyLocationContextState({
    ...searchState,
    persona: journeyTabsPersonaId,
  }), [searchState, journeyTabsPersonaId])
  const selectedPersona = content.personas.find((persona) => persona.id === searchState.persona)
    || getJourneyPersona(searchState.persona)
    || content.personas[0]
  const useOfferingRail = selectedPersona.offeringCards.length > 3

  useEffect(() => {
    const savedState = loadJourneyLastSearchState()
    if (!savedState) return
    if (hasUrlSpecifiedJourneySearchState(routeSearchState)) return
    setSearchState((previousState) => normalizeJourneySearchState({
      ...previousState,
      ...savedState,
      persona: previousState.persona,
    }))
    setLocationInputValue(savedState.placeName || '')
  }, [query, routeSearchState])

  useEffect(() => {
    saveJourneyLastSearchState(searchState)
  }, [searchState])

  useEffect(() => {
    let ignore = false
    const loadLocation = async () => {
      if (searchState.placeName || searchState.aroundLatLng) {
        setIsDetectingLocation(false)
        return
      }
      try {
        const location = await detectJourneyLocationFromIp()
        if (!ignore && location) {
          setSearchState((previousState) => normalizeJourneySearchState({
            ...previousState,
            ...location,
          }))
          setLocationInputValue(location.placeName)
        }
      } catch (error) {
        // Silent failure keeps the hero usable without location hydration.
      } finally {
        if (!ignore) setIsDetectingLocation(false)
      }
    }
    loadLocation()
    return () => {
      ignore = true
    }
  }, [searchState.aroundLatLng, searchState.placeName])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const hash = window.location.hash.replace(/^#/, '')
    if (hash !== PGA_COACH_LANDING_GUIDES_SECTION_ID) return undefined
    const node = document.getElementById(PGA_COACH_LANDING_GUIDES_SECTION_ID)
    if (!node) return undefined
    const frame = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [content.guidesItems.length])

  useEffect(() => {
    let ignore = false
    const loadLocations = async () => {
      if (!debouncedLocationInput || debouncedLocationInput === searchState.placeName) {
        if (!ignore) setLocationOptions([])
        return
      }
      const hits = await autocompleteCity(debouncedLocationInput)
      if (ignore) return
      setLocationOptions(hits.map(mapAlgoliaCityHitToLocationOption))
    }
    loadLocations()
    return () => {
      ignore = true
    }
  }, [debouncedLocationInput, searchState.placeName])

  useEffect(() => {
    let ignore = false
    const loadCoaches = async () => {
      setIsLoadingCoaches(true)
      try {
        const coaches = await searchLocalCoaches({
          aroundLatLng: searchState.aroundLatLng,
          hitsPerPage: 3,
        })
        if (!ignore) setLocalCoaches(coaches)
      } catch (error) {
        if (!ignore) setLocalCoaches([])
      } finally {
        if (!ignore) setIsLoadingCoaches(false)
      }
    }
    loadCoaches()
    return () => {
      ignore = true
    }
  }, [searchState.aroundLatLng])

  const handlePersonaChange = (personaId, source = 'tab') => {
    amplitude.track(source === 'hero'
      ? PGA_COACH_JOURNEY_ANALYTICS.heroPersonaSelect
      : PGA_COACH_JOURNEY_ANALYTICS.personaTabClick, {
      place_name: searchState.placeName,
      from_persona: searchState.persona,
      to_persona: personaId,
    })
    setHeroPersonaValue(personaId)
    setSearchState((previousState) => ({ ...previousState, persona: personaId }))
  }

  const applyLocationOption = useCallback((option) => {
    setSearchState((previousState) => normalizeJourneySearchState({
      ...previousState,
      placeName: option.placeName,
      aroundLatLng: option.aroundLatLng,
    }))
    setLocationInputValue(option.placeName)
  }, [])

  const handleLocationSelect = useCallback((option) => {
    if (option == null) return
    if (typeof option !== 'string') {
      applyLocationOption(option)
      return
    }
    const typed = option.trim()
    if (!typed) return
    setLocationInputValue(typed)
    const settleLocationFromInput = async () => {
      const resolved = await resolveLocationOptionFromInput(typed, locationOptionsRef.current)
      if (resolved) applyLocationOption(resolved)
    }
    settleLocationFromInput().catch(() => {})
  }, [applyLocationOption])

  const handleSearch = () => {
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.heroSearch, {
      personaId: searchState.persona,
      placeName: searchState.placeName,
    })
  }

  const resultsHrefForCard = (card) => buildJourneyOfferingCardSearchUrl(linkSearchState, card)

  const sectionHeading = formatJourneyHeading(
    selectedPersona.headlineTemplate,
    searchState.placeName || 'your area',
  )

  const buildCityCoachingHref = (city) => buildCityProgramsHref(journeyTabsPersonaId, city)

  const renderOfferingCard = (card, className = '') => {
    const baseImageUrl = card.imageUrl || defaults.facilityImage
    const optimizeSource = baseImageUrl.includes('?') ? baseImageUrl : `${baseImageUrl}?q=80`

    return (
      <div
        key={`${selectedPersona.id}-${card.title}`}
        className={`${classes.offeringCard} ${className}`.trim()}
      >
        <InfoCardVariant
          precedingHeading="Coaching Option"
          heading={card.title}
          cardImage={{
            url: composeOptimizedImageUrl(optimizeSource, 900),
            description: card.imageAlt || card.title,
          }}
          hideImageOverlay
          overlayToggleShowLabel="See"
          overlayToggleSubject="Programs"
          overlayToggleHref={resultsHrefForCard(card)}
          overlayToggleOnClick={() => {
            amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.offeringClick, {
              source: 'landing-card',
              personaId: selectedPersona.id,
              offeringTitle: card.title,
              format: card.format,
            })
          }}
        />
      </div>
    )
  }

  const getGuidesPersonaIds = useCallback((article) => {
    const articleTags = article?.tagsCollection?.items || []
    const normalizedTags = articleTags
      .map((tag) => normalizePersonaKey(tag?.label || ''))
      .filter(Boolean)

    if (normalizedTags.length) {
      const matchedPersonas = content.personas.filter((persona) => (
        personaMatchesNormalizedTags(persona, normalizedTags)
      ))
      if (matchedPersonas.length) {
        return matchedPersonas.map((persona) => persona.id)
      }
    }

    const articleCategory = article?.cat?.series || article?.cat?.category || ''
    const normalizedCategory = normalizePersonaKey(articleCategory)
    const matchingPersona = content.personas.find((persona) => (
      normalizedPersonaIdentifiers(persona).includes(normalizedCategory)
    ))

    return matchingPersona ? [matchingPersona.id] : []
  }, [content.personas])

  return (
    <div className={classes.root}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={content.metaTitle}
        description={content.metaDescription}
        canonical={`${defaults.baseUrl}coach`}
        openGraph={{
          title: content.metaTitle,
          type: 'website',
          url: `${defaults.baseUrl}coach`,
          images: content.metaImage?.url ? [{ url: content.metaImage.url }] : [],
        }}
      />
      <JsonLd {...{
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': 'https://www.pga.com/',
              name: 'PGA Of America',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': 'https://www.pga.com/coach',
              name: 'PGA Coach',
            },
          },
        ],
      }}
      />
      <section
        className={classes.hero}
        style={{ backgroundImage: `url(${content.metaImage?.url || defaults.facilityImage})` }}
      >
        <div className={classes.heroOverlay} />
        <Container className={classes.heroContent}>
          <Typography variant="h3" align="center" className={classes.heroHeadline}>
            {content.headline}
          </Typography>
          <Typography variant="h6" align="center" className={classes.heroSummary}>
            {content.subheadline}
          </Typography>
          <JourneySearchControls
            classes={classes}
            // Explicit persona only — unset hero leaves All results (no Start Golf injection).
            searchState={searchState}
            heroPersonaValue={heroPersonaValue}
            onPersonaChange={handlePersonaChange}
            onLocationInputChange={setLocationInputValue}
            onLocationSelect={handleLocationSelect}
            locationInputValue={locationInputValue}
            isDetectingLocation={isDetectingLocation}
            onSubmit={handleSearch}
            submitLabel={isCoachJourneyXsLayout ? (selectedPersona.ctaLabelMobile || selectedPersona.ctaLabel) : selectedPersona.ctaLabel}
            secondaryLinkLabel={content.viewAllCoachesLabel}
            locationOptions={{
              locations: locationOptions,
              personas: content.personas,
            }}
          />
        </Container>
      </section>

      <section className={classes.sectionShell}>
        <Container className={classes.section}>
          <Typography variant="h4" align="center" className={classes.sectionHeading}>
            {sectionHeading}
          </Typography>
          <Typography align="center" className={classes.sectionSummary}>
            {content.personaSectionSummary}
          </Typography>
          <CategoryTabs
            dense={isGuidesMobileLayout}
            items={content.personas.map((persona) => ({
              id: persona.id,
              label: persona.label,
            }))}
            value={journeyTabsPersonaId}
            onChange={(value) => handlePersonaChange(value, 'tab')}
            ariaLabel="PGA Coach journey personas"
          />
          <Box className={classes.tabsDivider} />
          {useOfferingRail ? (
            <SliderNew
              className={classes.offeringRail}
              config={offeringRailSliderConfig}
              showControls={showJourneyRailControls}
            >
              {selectedPersona.offeringCards.map((card) => renderOfferingCard(card, classes.offeringRailCard))}
            </SliderNew>
          ) : (
            <div className={classes.offeringGrid}>
              {selectedPersona.offeringCards.map((card) => (
                renderOfferingCard(card, classes.offeringGridCard)
              ))}
            </div>
          )}
          <div className={classes.centeredCta}>
            <ButtonCta
              color="primary"
              variant="contained"
              className={`${classes.resultsButton} ${classes.sectionPrimaryCta}`}
              href={buildIndexableJourneySearchUrl(linkSearchState)}
              onClick={handleSearch}
            >
              {isCoachJourneyXsLayout ? (selectedPersona.ctaLabelMobile || selectedPersona.ctaLabel) : selectedPersona.ctaLabel}
            </ButtonCta>
          </div>
        </Container>
      </section>

      <section className={classes.railSection}>
        <Container>
          <div className={classes.railHeader}>
            <div className={classes.railHeaderContent}>
              <Typography variant="h4" className={`${classes.sectionHeading} ${classes.railHeading}`}>
                {content.coachRailHeadline}
              </Typography>
              <Typography className={`${classes.sectionSummary} ${classes.railSummary}`}>
                {content.coachRailSummary}
              </Typography>
            </div>
            <Box className={classes.railCtaDesktop}>
              <ButtonCta
                className={classes.railLinkCta}
                variant="text"
                color="primary"
                href={LEGACY_COACH_DIRECTORY_HREF}
                dataAttrs={{ 'data-testid': 'coach-rail-view-all-link' }}
                arrow
              >
                {content.viewAllCoachesLabel}
              </ButtonCta>
            </Box>
          </div>
          <Box className={classes.coachRailBody}>
            {isLoadingCoaches && (
              <Box display="flex" justifyContent="center" className={classes.coachRailLoading}>
                <CircularProgress color="secondary" />
              </Box>
            )}
            {!isLoadingCoaches && localCoaches.length > 0 && (
              <div className={classes.coachRailSliderContainer}>
                <SliderNew
                  className={classes.coachRailSlider}
                  config={COACH_RAIL_SLIDER_CONFIG}
                  showControls={showJourneyRailControls}
                >
                  {localCoaches.map((coach) => (
                    <CoachCardOptimized
                      key={coach.objectID}
                      coachInfo={coach}
                      source="pga coach journey"
                    />
                  ))}
                </SliderNew>
              </div>
            )}
            {!isLoadingCoaches && localCoaches.length === 0 && (
              <Box textAlign="center" className={classes.coachRailEmpty}>
                <Typography variant="body1" color="textSecondary">
                  No nearby coaches are available right now. You can still explore the full coach directory.
                </Typography>
              </Box>
            )}
          </Box>
          <div className={classes.railCtaMobile}>
            <ButtonCta
              className={classes.sectionPrimaryCta}
              variant="contained"
              color="primary"
              href={LEGACY_COACH_DIRECTORY_HREF}
              dataAttrs={{ 'data-testid': 'coach-rail-view-all-link' }}
            >
              {content.viewAllCoachesLabel}
            </ButtonCta>
          </div>
          <div className={classes.trustPanel}>
            <Typography variant="h4" align="center" className={`${classes.sectionHeading} ${classes.trustHeading}`}>
              {content.trustHeadline}
            </Typography>
            <Typography align="center" className={`${classes.sectionSummary} ${classes.trustSummary}`}>
              {content.trustSummary}
            </Typography>
            <Box pt={4}>
              <Grid container spacing={3} justifyContent="center">
                {content.trustPillars.map((pillar) => {
                  const IconComponent = pillar.icon
                  return (
                    <Grid item xs={12} md={6} key={pillar.id}>
                      <div className={classes.trustCard}>
                        <div className={classes.trustIconShell}>
                          <IconComponent className={classes.trustIcon} />
                        </div>
                        <Typography variant="h6" className={classes.trustCardTitle}>{pillar.title}</Typography>
                        <Typography variant="body2" className={classes.trustCardDescription}>
                          {pillar.description}
                        </Typography>
                      </div>
                    </Grid>
                  )
                })}
              </Grid>
            </Box>
          </div>
        </Container>
      </section>

      {content.guidesItems.length > 0 && (
        <section
          id={PGA_COACH_LANDING_GUIDES_SECTION_ID}
          className={classes.guidesSectionShell}
        >
          <div className={classes.guidesArticlesHost}>
            <CoachLandingArticles
              className={classes.guidesSectionContent}
              disableDefaultMargins
              headline={content.guidesHeadline}
              summary={content.guidesSummary}
              railConfig={guidesRailSliderConfig}
              items={content.guidesItems}
              isMobile={isGuidesMobileLayout}
              enableTabs
              centerHeader
              headlineVariant="h3"
              tabItems={content.personas.map((persona) => ({
                id: persona.id,
                label: persona.label,
              }))}
              selectedTabId={journeyTabsPersonaId}
              onTabChange={(personaId) => handlePersonaChange(personaId, 'tab')}
              getItemTabIds={getGuidesPersonaIds}
            />
          </div>
        </section>
      )}

      <section className={classes.citySectionShell}>
        <Container className={classes.citySection}>
          <Typography variant="h4" align="center" className={`${classes.sectionHeading} ${classes.citySectionHeadline}`}>
            {content.citySectionHeadline}
          </Typography>
          <Typography align="center" className={classes.citySectionSummary}>
            {content.citySectionSummary}
          </Typography>
          <div className={classes.cityPills}>
            {content.cityLinks.map((city) => (
              <ButtonCta
                key={city.slug}
                href={buildCityCoachingHref(city)}
                variant="text"
                color="primary"
                className={classes.cityPill}
              >
                {city.label}
              </ButtonCta>
            ))}
          </div>
          <div className={classes.citySectionFooter}>
            <ButtonCta
              href={buildJourneySearchUrl({ persona: searchState.persona }, PGA_COACH_PROGRAMS_SEARCH_PATHNAME)}
              variant="contained"
              color="primary"
              className={classes.sectionPrimaryCta}
            >
              {content.citySectionCta}
            </ButtonCta>
          </div>
        </Container>
      </section>
    </div>
  )
}

PgaCoachJourney.propTypes = {
  ssrData: PropTypes.object,
  query: PropTypes.object,
}

export default PgaCoachJourney
